import { message, Typography } from 'antd'
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint'
import api from 'api'
import { DefaultLayout } from 'components/layouts/DefaultLayout'
import { ArtWorks } from 'components/models/artworks/ArtWorks'
import { EventInformation } from 'components/pages/event/details/EventInformation'
import { FixedTitleHeader } from 'components/pages/event/details/FixedTitleHeader'
import { Loading } from 'components/shared/Loading'
import { NextHead } from 'components/shared/NextHead'
import { ScrollTopButton } from 'components/shared/ScrollTopButton'
import colors from 'helpers/colors'
import { urls } from 'helpers/urls'
import { useArtistsByEventId } from 'hooks/artists'
import { useArtworksByEventId } from 'hooks/artworks'
import { useEvent } from 'hooks/events'
import { first, throttle } from 'lodash'
import { GetServerSideProps } from 'next'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { NextPageWithHead } from 'pages/_app'
import { useEffect, useMemo, useRef, useState } from 'react'

import { LimitedEventPage } from '../../../components/pages/event/details/LimitedEventPage/LimitedEventPage'

const ART_WORK_LIMIT = 18

type Props = {
  event?: Event
}

const Page: NextPageWithHead<Props> = () => {
  const { xs } = useBreakpoint()
  const router = useRouter()
  const { t } = useTranslation()

  const searchObject = useMemo(() => {
    const params = new URLSearchParams(router.asPath.split(/\?/)[1])
    const searchObject = {}

    for (const key of Array.from(params.keys())) {
      if (key.includes('_in')) {
        Object.assign(searchObject, { [key]: params.getAll(key) })
      } else {
        Object.assign(searchObject, { [key]: params.get(key) })
      }
    }
    return searchObject
  }, [router.asPath])

  const eventId = router.isReady ? (router.query.id as string) : undefined
  const {
    data: event,
    error: fetchEventError,
    mutate: mutateEvent,
  } = useEvent(eventId)
  const {
    data: artWorksData,
    error: fetchArtworksError,
    isValidating: isValidatingArtworks,
    setSize: setSizeArtworks,
  } = useArtworksByEventId(eventId, ART_WORK_LIMIT, searchObject)
  const artworks = artWorksData?.flatMap((v) => v.artWorksData) || []

  const { data: artists = [] } = useArtistsByEventId(eventId)

  const [isShowFixedTitleHeader, setIsShowFixedTitleHeader] = useState(false)
  // 作品リストまでスクロールした時にイベントタイトルを表示するためにrefで高さを取得する
  const artworksSectionRef = useRef<HTMLDivElement>(null)

  const throttleCheckScrollTop = throttle(() => {
    if (!artworksSectionRef.current) return

    const scrollY = window.scrollY
    if (scrollY > artworksSectionRef.current?.offsetTop) {
      setIsShowFixedTitleHeader(true)
    }
    if (scrollY <= artworksSectionRef.current?.offsetTop) {
      setIsShowFixedTitleHeader(false)
    }
  }, 100)

  useEffect(() => {
    window.addEventListener('scroll', throttleCheckScrollTop)
    return () => {
      window.removeEventListener('scroll', throttleCheckScrollTop)
    }
  }, [throttleCheckScrollTop])

  useEffect(() => {
    if (eventId === undefined) {
      return
    }

    if (!fetchEventError) {
      return
    }

    if (fetchArtworksError) {
      message.error(t('作品一覧の取得に失敗しました。'))
    }

    switch (fetchEventError.status) {
      case 401:
        router.replace({
          pathname: '/events/[id]/unlock',
          query: { id: eventId },
        })
        break
      case 404:
        router.push(urls.top)
        break
      default:
        break
    }
  }, [router, fetchEventError, eventId, t, fetchArtworksError])

  // WARN: isValidatingEventはリクエスト前はfalseであるため、eventの有無で判断する
  if (!event) {
    return (
      <DefaultLayout onlyLogo={false}>
        <div style={{ marginTop: '20px' }}>
          <Loading />
        </div>
      </DefaultLayout>
    )
  }

  if (!event.visible_to_user) {
    return (
      <DefaultLayout onlyLogo={false}>
        <LimitedEventPage event={event} mutateEvent={mutateEvent} />
      </DefaultLayout>
    )
  }

  const breadcrumbs = [
    { text: t('ホーム'), href: urls.top },
    { text: t('イベント一覧'), href: urls.events },
    { text: event.title },
  ]

  return (
    <DefaultLayout onlyLogo={false} breadCrumbs={breadcrumbs}>
      {isShowFixedTitleHeader && <FixedTitleHeader title={event.title} />}
      <div style={{ padding: xs ? '0px 0px 80px' : '40px 64px 120px' }}>
        <EventInformation event={event} artists={artists} />
        <div
          style={{
            borderBottom: `1px solid ${colors.border}`,
            margin: xs ? '0px 16px' : '0px',
            padding: xs ? '8px 16px' : '0px 0px 16px',
          }}
        >
          <Typography.Text style={{ fontSize: '22px', lineHeight: 1.5 }} strong>
            {t('作品')}
          </Typography.Text>
        </div>

        <div style={{ padding: xs ? '0px 16px' : 0 }}>
          <ArtWorks
            artWorks={artworks}
            isValidatingArtworks={isValidatingArtworks}
            setSizeArtworks={setSizeArtworks}
            artWorkTotalCount={first(artWorksData)?.xTotalCount || 0}
            xs={xs}
            artists={artists}
          />
        </div>
      </div>
      <ScrollTopButton />
    </DefaultLayout>
  )
}

export default Page

Page.getHead = ({ event }) => {
  return (
    <NextHead
      title={`${event?.title}（${event?.owner.name}）｜ArtX`}
      type="article"
      thumbnailUrl={event?.main_event_image_url}
      description={event?.description}
    />
  )
}

export const getServerSideProps: GetServerSideProps<Props> = async ({
  locale,
  params,
}) => {
  const { data: event } = await api.get<Event>(`/events/${params?.id}`)

  return {
    props: {
      event,
      ...(await serverSideTranslations(locale ?? 'ja', ['common'])),
    },
  }
}
